import type {ReactElement} from 'react';
import React, {useEffect, useRef} from 'react';
import {getUniqueString} from '@Utils/string.util';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import {Label} from '@Components/label';
import {ToggleSwitch} from '@Components/toggle-switch-v2';
import type {ShapeBackendData} from '@Panels/masking-panel/masking-modal.types';
import {clearSelectedItems, setGridItems, toggleGridItemSelection} from '@Components/base-grid';
import {v4 as uuidv4} from 'uuid';
import type {GridShapeItemStorage} from '@Components/base-grid/components/grid-shape-item';
import {StandardGrid} from '@Components/standard-grid';
import {setGridID} from '@Panels/masking-panel/components/shape-masking-panel/shape-masking-panel-reducer';
import {useAppDispatch, useAppSelector} from '@/hooks';
import styles from './shape-masking-editing-panel.module.scss';

export interface ShapeMaskingEditingPanelProps {
  isSmall: boolean;
  className?: string;
}

interface AdditionalShapeData {
  shapeName: string;
}

function ShapeMaskingEditingPanel({...props}: ShapeMaskingEditingPanelProps): ReactElement {
  const gridId = useRef(getUniqueString());
  const gridSelectionGroupId = useRef(gridId.current);
  const contentRef = useRef<HTMLDivElement>(null);
  const id = uuidv4();
  const shapeMaskData = useAppSelector((state) => {
    return state.shapeMasking;
  });

  const dispatch = useAppDispatch();

  useEffect(() => {
    void getGridItems();
    dispatch(setGridID(gridId.current));
  }, []);

  const getTogglesContainer = (): ReactElement => {
    return (
      <div className={styles.togglesContainer}>
        <div className={`${styles.toggleContainer}`}>
          <Label text="Invert" />
          <ToggleSwitch
            id={`${id}-invert`}
            isChecked={shapeMaskData.hasInsideMasking}
            onChange={(): void => {
              void window.shapeMask?.toggleInsideMasking();
            }}
          />
        </div>
        <div className={`${styles.toggleContainer}`}>
          <Label text="Color Pop" />
          <ToggleSwitch
            id={`${id}-color-pop`}
            isChecked={shapeMaskData.hasColorPop}
            onChange={(): void => {
              void window.shapeMask?.toggleMaskingEffect();
            }}
          />
        </div>
      </div>
    );
  };

  const getGridItems = async (): Promise<null> => {
    const data = (await window.PMW.readLocal('pmwstockmedia/getPMWStockMedia', {
      types: 'shape',
      showHidden: false,
      sortBy: 'admin_score_desc',
      isUsedForMasking: true,
    })) as Array<ShapeBackendData>;
    const items: Array<GridShapeItemStorage> = data.map((item) => {
      return {
        id: `${item.idPMWStockShape}`,
        type: GRID_ITEM_TYPE.PMW_STOCK_SHAPE,
        hashedFilename: `${item.idPMWStockShape}`,
        additionalData: {
          shapeName: `${item.idPMWStockShape}`,
        } as AdditionalShapeData,
      };
    });
    dispatch(
      setGridItems({
        id: gridId.current,
        gridSelectionGroupId: gridId.current,
        items,
      })
    );
    return null;
  };

  // TODO: Grid should a controlled component in this case so that the selected grid item is passed from this component, but due
  // TODO: to time constraints I have used useEffect rather than converting the grid to a controlled component
  useEffect(() => {
    if (shapeMaskData.shapeId === undefined) {
      dispatch(
        clearSelectedItems({
          gridSelectionGroupId: gridSelectionGroupId.current,
        })
      );
      return;
    }

    dispatch(
      toggleGridItemSelection({
        gridItemId: shapeMaskData.shapeId,
        gridSelectionGroupId: gridSelectionGroupId.current,
      })
    );
  }, [shapeMaskData.shapeId]);

  const onGridItemClicked = (gridItem: GridShapeItemStorage): void => {
    void window.shapeMask?.addNewShape(gridItem.id);
  };

  const getShapesContainer = (): ReactElement => {
    return (
      <div className={`${styles.gridContainer} ${props.isSmall ? `${styles.smallGrid}` : ''}`} ref={contentRef}>
        <StandardGrid
          customOnGridItemClicked={onGridItemClicked}
          gridSelectionGroupId={gridSelectionGroupId.current}
          gridId={gridId.current}
          gridItemMaxWidth={140}
          maxSelectionNumber={1}
        />
      </div>
    );
  };

  return (
    <div className={`flex-v-row ${styles.editingPanel} ${props.isSmall ? styles.smallScreen : '_full-height'} ${props.isSmall ? '_full-width ' : styles.desktopWidth}`}>
      {props.isSmall ? getShapesContainer() : getTogglesContainer()}
      {props.isSmall ? null : <h4 className={`spacing-m-t-6 spacing-m-b-3 spacing-p-b-2 ${styles.heading}`}>Select a shape</h4>}
      {props.isSmall ? getTogglesContainer() : getShapesContainer()}
    </div>
  );
}

export default ShapeMaskingEditingPanel;
